import moment from 'moment-timezone';
import set from 'lodash/set';
import { IsArray, IsBoolean, IsDateString, IsNotEmpty, IsString, ValidateNested, IsOptional } from 'class-validator';
import { Type } from 'class-transformer';

import OfficeHours, { ScheduleBlock } from './OfficeHours';
import { HasNoOverlappingRanges } from './validation/HasNoOverlappingRanges';
import { IsBusinessHoursExceptionHasNoDuplicates } from './validation/IsBusinessHoursExceptionUnique';
import { IsStartBeforeStopDateTime } from './validation/IsStartBeforeStopDateTime';
import { IsStringPropertyUniqueInStoreSlice } from 'src/actions/validators/IsStringPropertyUniqueInStoreSlice';

class BusinessHours {
  id: string;

  @IsString()
  @IsNotEmpty({ message: 'Business Hours require a name' })
  @IsStringPropertyUniqueInStoreSlice('businessHours', { message: 'This name already exists' })
  name: string;

  primary: boolean;
  version: number;

  @ValidateNested({ each: true })
  @Type(() => BusinessOfficeHours)
  officeHours: BusinessOfficeHours;

  createdAt: string;
  createdBy: ByUser;
  updatedAt: string;
  updatedBy: ByUser;
}

class ByUser {
  id: string;
  type: string;
}

export class BusinessOfficeHours extends OfficeHours {
  @IsString()
  @IsNotEmpty({ message: 'Select a timezone' })
  timezone: string;

  @IsArray()
  @ValidateNested({ each: true })
  @HasNoOverlappingRanges()
  @Type(() => ScheduleBlock)
  monday: ScheduleBlock[];

  @IsArray()
  @ValidateNested({ each: true })
  @HasNoOverlappingRanges()
  @Type(() => ScheduleBlock)
  tuesday: ScheduleBlock[];

  @IsArray()
  @ValidateNested({ each: true })
  @HasNoOverlappingRanges()
  @Type(() => ScheduleBlock)
  wednesday: ScheduleBlock[];

  @IsArray()
  @ValidateNested({ each: true })
  @HasNoOverlappingRanges()
  @Type(() => ScheduleBlock)
  thursday: ScheduleBlock[];

  @IsArray()
  @ValidateNested({ each: true })
  @HasNoOverlappingRanges()
  @Type(() => ScheduleBlock)
  friday: ScheduleBlock[];

  @IsArray()
  @ValidateNested({ each: true })
  @HasNoOverlappingRanges()
  @Type(() => ScheduleBlock)
  saturday: ScheduleBlock[];

  @IsArray()
  @ValidateNested({ each: true })
  @HasNoOverlappingRanges()
  @Type(() => ScheduleBlock)
  sunday: ScheduleBlock[];

  @ValidateNested({ each: true })
  @IsBusinessHoursExceptionHasNoDuplicates()
  @Type(() => ScheduleException)
  @IsOptional()
  exceptions: ScheduleException[];
}

export class ScheduleException {
  @IsString()
  @IsNotEmpty({ message: 'Exceptions require a name' })
  name: string;

  @IsDateString({}, { message: 'A date and time range is required' })
  @IsStartBeforeStopDateTime()
  start: string;

  @IsDateString({}, { message: 'A date and time range is required' })
  stop: string;

  @IsBoolean()
  closed: boolean;
}

class ConfigurationDefaults {
  constructor() {
    set(this, 'version', 1);
    set(this, 'officeHours.timezone', moment.tz.guess());
    set(this, 'officeHours.friday', [{ start: '09:00', stop: '17:00' }]);
    set(this, 'officeHours.thursday', [{ start: '09:00', stop: '17:00' }]);
    set(this, 'officeHours.wednesday', [{ start: '09:00', stop: '17:00' }]);
    set(this, 'officeHours.tuesday', [{ start: '09:00', stop: '17:00' }]);
    set(this, 'officeHours.monday', [{ start: '09:00', stop: '17:00' }]);
    set(this, 'officeHours.sunday', []);
    set(this, 'officeHours.saturday', []);
    set(this, 'officeHours.exceptions', []);
  }
}

export const businessHoursConfigurationDefaults = Object.freeze(new ConfigurationDefaults()) as BusinessHours;

export default BusinessHours;
